import React from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  styled,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  CircularProgress,
} from "@material-ui/core";
import { colors } from "../../utilities/src/Colors";

import BannerCreateController, {
  S,
  Props,
} from "./BannerCreateController.web";

const navigationTypes = [
  { value: "product", label: "Product" },
  { value: "category", label: "Category" },
  { value: "screen", label: "Screen" },
  { value: "external_url", label: "External URL" },
];

export default class BannerCreate extends BannerCreateController {
  constructor(props: Props) {
    super(props);
    this.state = {
      form: {
        title: "",
        is_active: true,
        position: "0",
        is_navigable: false,
        navigation_type: "",
        navigation_id: "",
        external_url: "",
        start_date: "",
        end_date: "",
        image_en: null,
        image_ar: null,
      },
      imageEnPreview: "",
      imageArPreview: "",
      isSubmitting: false,
      errors: {},
    } as S;
  }

  render() {
    const { form, errors, isSubmitting, imageEnPreview, imageArPreview } =
      this.state;

    return (
      <MainWrapper>
        <Box className="pageWrapper" style={{ paddingTop: "10px" }}>
          <div style={webStyle.headerWrapper}>
            <PageTitle>Add Banner</PageTitle>
          </div>

          <Paper style={webStyle.formPaper}>
            <Grid container spacing={3}>
              {/* Title */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="titleInput"
                  label="Title"
                  variant="outlined"
                  fullWidth
                  value={form.title}
                  onChange={(e) =>
                    this.handleFormChange("title", e.target.value)
                  }
                  error={!!errors.title}
                  helperText={errors.title}
                />
              </Grid>

              {/* Position */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="positionInput"
                  label="Position"
                  variant="outlined"
                  fullWidth
                  type="number"
                  value={form.position}
                  onChange={(e) =>
                    this.handleFormChange("position", e.target.value)
                  }
                />
              </Grid>

              {/* Is Active */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      data-test-id="isActiveSwitch"
                      checked={form.is_active}
                      onChange={(e) =>
                        this.handleFormChange("is_active", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Grid>

              {/* Is Navigable */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      data-test-id="isNavigableSwitch"
                      checked={form.is_navigable}
                      onChange={(e) =>
                        this.handleFormChange("is_navigable", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Navigable"
                />
              </Grid>

              {/* Navigation Type */}
              {form.is_navigable && (
                <Grid item xs={12} sm={6}>
                  <FormControl
                    variant="outlined"
                    fullWidth
                    error={!!errors.navigation_type}
                  >
                    <InputLabel>Navigation Type</InputLabel>
                    <Select
                      data-test-id="navigationTypeSelect"
                      value={form.navigation_type}
                      onChange={(e) =>
                        this.handleFormChange(
                          "navigation_type",
                          e.target.value as string
                        )
                      }
                      label="Navigation Type"
                    >
                      {navigationTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.navigation_type && (
                      <FormHelperText>{errors.navigation_type}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              )}

              {/* Navigation ID */}
              {form.is_navigable &&
                ["product", "category", "screen"].includes(
                  form.navigation_type
                ) && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      data-test-id="navigationIdInput"
                      label="Navigation ID"
                      variant="outlined"
                      fullWidth
                      value={form.navigation_id}
                      onChange={(e) =>
                        this.handleFormChange("navigation_id", e.target.value)
                      }
                      error={!!errors.navigation_id}
                      helperText={errors.navigation_id}
                    />
                  </Grid>
                )}

              {/* External URL */}
              {form.is_navigable &&
                form.navigation_type === "external_url" && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      data-test-id="externalUrlInput"
                      label="External URL"
                      variant="outlined"
                      fullWidth
                      value={form.external_url}
                      onChange={(e) =>
                        this.handleFormChange("external_url", e.target.value)
                      }
                      error={!!errors.external_url}
                      helperText={errors.external_url}
                    />
                  </Grid>
                )}

              {/* Start Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="startDateInput"
                  label="Start Date"
                  variant="outlined"
                  fullWidth
                  type="date"
                  value={form.start_date}
                  onChange={(e) =>
                    this.handleFormChange("start_date", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* End Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="endDateInput"
                  label="End Date"
                  variant="outlined"
                  fullWidth
                  type="date"
                  value={form.end_date}
                  onChange={(e) =>
                    this.handleFormChange("end_date", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Image EN */}
              <Grid item xs={12} sm={6}>
                <ImageUploadBox>
                  <InputLabel style={webStyle.uploadLabel}>
                    Image (English) *
                  </InputLabel>
                  <input
                    data-test-id="imageEnInput"
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={(e) => this.handleImageChange("image_en", e)}
                    style={webStyle.fileInput}
                  />
                  {imageEnPreview && (
                    <img
                      src={imageEnPreview}
                      alt="EN Preview"
                      style={webStyle.imagePreview}
                    />
                  )}
                  {errors.image_en && (
                    <FormHelperText error>{errors.image_en}</FormHelperText>
                  )}
                </ImageUploadBox>
              </Grid>

              {/* Image AR */}
              <Grid item xs={12} sm={6}>
                <ImageUploadBox>
                  <InputLabel style={webStyle.uploadLabel}>
                    Image (Arabic) *
                  </InputLabel>
                  <input
                    data-test-id="imageArInput"
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={(e) => this.handleImageChange("image_ar", e)}
                    style={webStyle.fileInput}
                  />
                  {imageArPreview && (
                    <img
                      src={imageArPreview}
                      alt="AR Preview"
                      style={webStyle.imagePreview}
                    />
                  )}
                  {errors.image_ar && (
                    <FormHelperText error>{errors.image_ar}</FormHelperText>
                  )}
                </ImageUploadBox>
              </Grid>

              {/* Buttons */}
              <Grid item xs={12}>
                <div style={webStyle.buttonRow}>
                  <Button
                    data-test-id="cancelButton"
                    variant="outlined"
                    onClick={this.handleCancel}
                    style={webStyle.cancelButton}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-test-id="submitButton"
                    variant="contained"
                    onClick={this.handleSubmit}
                    disabled={isSubmitting}
                    style={webStyle.submitButton}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} style={{ color: "#fff" }} />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </MainWrapper>
    );
  }
}

// Customizable Area Start
const PageTitle = styled("div")({
  fontFamily: "Montserrat",
  fontWeight: 600,
  fontSize: "24px",
  lineHeight: "29px",
  "@media only screen and (max-width: 1024px)": {
    fontSize: "16px",
  },
});

const MainWrapper = styled("div")({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
  },
});

const ImageUploadBox = styled("div")({
  border: "1px dashed #ccc",
  borderRadius: "8px",
  padding: "16px",
  textAlign: "center",
});

const webStyle = {
  headerWrapper: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 32,
    alignItems: "center",
  },
  formPaper: {
    padding: 32,
    borderRadius: 8,
  },
  uploadLabel: {
    marginBottom: 8,
    fontSize: 14,
    fontWeight: 600 as 600,
    color: "#333",
  },
  fileInput: {
    marginTop: 8,
  },
  imagePreview: {
    marginTop: 12,
    maxWidth: "100%",
    maxHeight: 150,
    objectFit: "contain" as "contain",
    borderRadius: 4,
  },
  buttonRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 16,
    marginTop: 16,
  },
  cancelButton: {
    borderColor: colors().cyancobaltblue,
    color: colors().cyancobaltblue,
    height: 56,
    width: 160,
    borderRadius: 8,
    textTransform: "none" as "none",
    fontWeight: 600,
    fontSize: 16,
  },
  submitButton: {
    background: colors().cyancobaltblue,
    color: "#fff",
    height: 56,
    width: 160,
    borderRadius: 8,
    textTransform: "none" as "none",
    fontWeight: 600,
    fontSize: 16,
  },
};
// Customizable Area End
